import { Permissions } from '../../../../models/enums/permissions';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { PageBody } from '../../../../components/page-components/PageBody';
import { PageContent } from '../../../../components/page-components/PageContent';
import { HasPermission } from '../../../../containers/authorization-helpers/HasPermission';
import { PageNames } from '../../../../utils/app-constants';
import { InventoryPhysicalReport, InventoryReportServiceCenterGroup } from '../../../../modules/inventory/models/inventory-physical-report';
import { InventorySvc } from '../../../../shared/services/inventory-service';
import { navigateToPage } from '../../../../utils/history';
import { Routes } from '../../../../utils/app-constants';
import { StatusCode } from '../../../../utils/api';
import { formatCurrency } from '../../../../utils/currency-formatter';
import { PhysicalInventoryStatus } from '../../../../modules/inventory/models/physical-inventory-enums';
import { Table } from '../../../../components/tables/Table';
import { getDocumentHelper } from '../../../../actions/utility-actions';
import { InventoryDataDocument } from '../../../../modules/inventory/models/inventory-data-document';

interface IProps {
    location: any;
}

export const InventoryPhysicalReportScene = React.forwardRef((props: IProps, ref) => {

    const dispatch = useDispatch();

    const [report, setReport] = React.useState(new InventoryPhysicalReport());
    const [service] = React.useState(new InventorySvc());

    function finalize() {
        var reportId = getReportId();
        service.finalizeReport(reportId).then(result => { getReport(reportId); });
    }

    function downloadData() {
        var reportId = getReportId();
        const req = new InventoryDataDocument(reportId);
        dispatch(getDocumentHelper(req));
    }

    function getReportId() {
        const opts = props.location.pathname.split('/');
        return opts.length > 4 ? opts[4] : null;
    }

    // initialization 
    React.useEffect(() => {
        getReport(getReportId());
    }, [props.location]);

    React.useImperativeHandle(ref, () => ({
        goBack() {
            goBack();
        }
    }))

    function goBack() {
        var reportId = getReportId();
        navigateToPage(Routes.INVENTORY_PHYSICAL_DETAILS_CONTROLGROUPS.formattify(reportId));
    }

    const getStatusName = (status: PhysicalInventoryStatus) => {
        switch (status) {
            case PhysicalInventoryStatus.complete:
                return 'COMPLETE';
            case PhysicalInventoryStatus.inProgress:
            default:
                return 'IN PROGRESS';
        }
    }

    async function getReport(reportId: string){
        if (reportId) {
            const result = await service.getFinalReport(reportId);
            if (result.status === StatusCode.Success) {
                setReport(result.data);
            }
        }
        else {
            goBack();
        }
    }

    function getValueClass(value: number) : string {

        if (value > 0) {
            return "positive-value"
        }
        else if (value < 0) {
            return "negative-value"
        }
        else {
            return "";
        }
    }

    const getButtons = () => {

        return <HasPermission requiredPermissions={[
            Permissions.InventoryPhysicalReportsEdit
        ]}>
            <button className="btn complete" onClick={() => finalize()}>
                FINALIZE REPORT
            </button>
            <button className="btn attention" onClick={() => downloadData()}>
                <i className="la la-file-excel-o" />
                DOWNLOAD DATA
            </button>
        </HasPermission>
    }

    return <PageContent pageName={PageNames.INVENTORY_PHYSICAL_REPORT} classes='inventory-physical'>
        <PageBody>
            <div className="row">
                <div className="col">
                    {getButtons()}
                </div>
            </div>
            <div className="row">
                <div className="col-1">
                </div>
                <div className="col-3">
                    <span className="invnetory-label">REPORT DATE</span>
                    <span className="invnetory-value">{report.date}</span>
                </div>
                <div className="col-3">
                    <span className="invnetory-label">Totals</span>
                    <span className={"invnetory-value " + getValueClass(report.totalValue)}>{formatCurrency(report.totalValue)}</span>
                </div>
                <div className="col-3">
                    <span className="invnetory-label">Status</span>
                    <span className="invnetory-value">{getStatusName(report.status)}</span>
                </div>
                <div className="col-2">
                </div>
            </div>
            {
                report.serviceCenterGroups.map((grp: InventoryReportServiceCenterGroup, grpIdx: number) => {
                    return <div className="row">
                        <div className="col">
                            <div className="row">
                                <div className="col report-header">
                                    <span>{grp.serviceCenterId}</span>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col">
                                    <Table
                                        noResultsMessage="No Service Centers in report."
                                        header={[
                                            "TYPE",
                                            "",
                                            "VALUE"
                                        ]}
                                    >
                                        <tr>
                                            <td style={{ width: '75%' }}><span>Raw Materials</span></td>
                                            <td style={{ width: '10%' }} className="report-total right"><span>+</span></td>
                                            <td style={{ width: '15%' }}><span>{formatCurrency(grp.controlGroupsValue)}</span></td>
                                        </tr>
                                        <tr>
                                            <td><span>Shipments</span></td>
                                            <td className="report-total right"><span>+</span></td>
                                            <td><span>{formatCurrency(grp.shipmentsValue)}</span></td>
                                        </tr>
                                        <tr>
                                            <td><span>Orders</span></td>
                                            <td className="report-total right"><span>+</span></td>
                                            <td><span>{formatCurrency(grp.ordersValue)}</span></td>
                                        </tr>
                                        <tr>
                                            <td><span>Adjustments</span></td>
                                            <td className="report-total right"><span>+</span></td>
                                            <td><span>{formatCurrency(grp.adjustmentsValue)}</span></td>
                                        </tr>
                                        <tr>
                                            <td></td>
                                            <td className="report-total right"><span>Subtotal</span></td>
                                            <td><span className="report-total">{formatCurrency(grp.totalValue)}</span></td>
                                        </tr>
                                    </Table>
                                </div>
                            </div>
                        </div>
                    </div>
                })
            }
            <div className="row">
                <div className="col-8">                    
                </div>
                <div className="col-2 report-footer">
                    <span>TOTAL</span>
                </div>
                <div className="col-2 report-footer">
                    <span>{formatCurrency(report.totalValue)}</span>
                </div>
            </div>
        </PageBody>
    </PageContent >
});